/**
 * The selection funnel: `select` is the one way a selection is set, and it
 * stores the selection in its canonical form (`normalizeSelection`), so a
 * segment can never be addressed two ways at once. The addressing rules
 * themselves live in edgeRefs.
 */

import { edgeSegments, type FloorPlanRoom } from '@kaiizen/kai-floor-plan';
import { segmentSelectionFor } from './edgeRefs';
import type { EditorMutableState, Selection } from './editorState';

/**
 * The canonical form of a selection against the room it addresses: a
 * primary-edge segment selected under the wrong kind (a door as an alcove, an
 * alcove as a line segment, an alcove corner or wall whose segment is no
 * longer an alcove) comes back addressed the one right way. Every other
 * selection passes through untouched, including ones pointing past the end
 * of an edge (drag commits re-resolve those by position).
 */
export function normalizeSelection(shape: FloorPlanRoom, selection: Selection): Selection {
  if (selection.kind === 'alcove' || selection.kind === 'alcoveCorner' || selection.kind === 'alcoveWall') {
    const segment = edgeSegments(shape, selection.edge)[selection.index];
    if (segment && segment.edgeSegmentType !== 'alcove') return segmentSelectionFor(selection.edge, selection.index, segment);
    return selection;
  }
  if (selection.kind === 'lineSeg' && selection.ref.k === 'pe') {
    const segment = edgeSegments(shape, selection.ref.edge)[selection.si];
    if (segment && segment.edgeSegmentType === 'alcove') return segmentSelectionFor(selection.ref.edge, selection.si, segment);
  }
  return selection;
}

/** Set the selection in its canonical form, clearing per-selection transient affordance state. */
export function select(state: EditorMutableState, selection: Selection): void {
  state.selection = normalizeSelection(state.shape, selection);
  state.editingKey = null;
  if (selection.kind !== 'edge') state.edgeGap = null;
  if (selection.kind !== 'alcoveWall') state.wallGap = null;
}
